import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Briefcase, BarChart3, TrendingUp, DollarSign, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { getSalesRepDashboardStats, getAdminDashboardStats, type DashboardStats } from '@/services/dashboardStats';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { dashboardPageTour } from '@/walkthrough/tourSteps';

// ===========================================
// Dashboard Page (Rep View)
// ===========================================

export function DashboardPage() {
  const { user, isOwnerOrAdmin } = useAuth();
  const { currentBusinessView, getBusinessViewLabel } = useBusiness();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(dashboardPageTour);
  const [stats, setStats] = useState<DashboardStats>({
    totalCompanies: 0,
    myProjects: 0,
    closedThisQuarter: 0,
    pendingCommission: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);

    const statsPromise = isOwnerOrAdmin
      ? getAdminDashboardStats(currentBusinessView)
      : getSalesRepDashboardStats(user.id);

    statsPromise
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, isOwnerOrAdmin, currentBusinessView]);

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.displayName?.split(' ')[0] || 'User'}!
        </h1>
        <p className="text-gray-600 mt-1">
          You're viewing <span className="font-medium">{getBusinessViewLabel(currentBusinessView)}</span> data.
          Use the toggle above to switch businesses.
        </p>
      </div>

      {/* Stats grid */}
      <div data-tour="dashboard-stats" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Referral Sources"
          value={loading ? '...' : stats.totalCompanies}
          icon={<Building2 className="text-blue-600" size={24} />}
          link="/companies"
          linkText="View all companies"
        />
        <StatCard
          title={isOwnerOrAdmin ? 'All Projects' : 'My Projects'}
          value={loading ? '...' : stats.myProjects}
          icon={<Briefcase className="text-green-600" size={24} />}
          link="/projects"
          linkText={isOwnerOrAdmin ? 'View all projects' : 'View my projects'}
        />
        <StatCard
          title="Closed This Quarter"
          value={loading ? '...' : stats.closedThisQuarter}
          icon={<TrendingUp className="text-purple-600" size={24} />}
          link="/reports"
          linkText="View reports"
        />
        <StatCard
          title="Pending Commission"
          value={loading ? '...' : `$${stats.pendingCommission.toLocaleString()}`}
          icon={<DollarSign className="text-yellow-600" size={24} />}
          link="/reports"
          linkText="View details"
        />
      </div>

      {/* Quick actions */}
      <div data-tour="quick-actions" className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <QuickActionCard
            title="Add Referral Source"
            description="Create a new company in the directory"
            icon={<Building2 size={32} />}
            link="/companies/new"
            color="blue"
          />
          <QuickActionCard
            title="View My Projects"
            description="See your assigned projects"
            icon={<Briefcase size={32} />}
            link="/projects"
            color="green"
          />
          <QuickActionCard
            title="Quarterly Report"
            description="View commission report"
            icon={<BarChart3 size={32} />}
            link="/reports"
            color="purple"
          />
        </div>
      </div>

      {/* Recent activity placeholder */}
      <div data-tour="recent-activity" className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="text-center py-8 text-gray-500">
          <Clock size={48} className="mx-auto mb-3 opacity-50" />
          <p>No recent activity</p>
          <p className="text-sm">Your recent actions will appear here</p>
        </div>
      </div>

      <WelcomeModal
        pageName={dashboardPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Stat Card Component
// ===========================================

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  link: string;
  linkText: string;
}

function StatCard({ title, value, icon, link, linkText }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-full">{icon}</div>
      </div>
      <Link
        to={link}
        className="mt-4 inline-block text-sm text-blue-600 hover:text-blue-800"
      >
        {linkText} &rarr;
      </Link>
    </div>
  );
}

// ===========================================
// Quick Action Card Component
// ===========================================

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  color: 'blue' | 'green' | 'purple' | 'yellow';
}

function QuickActionCard({ title, description, icon, link, color }: QuickActionCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
    green: 'bg-green-50 text-green-600 hover:bg-green-100',
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
    yellow: 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100',
  };

  return (
    <Link
      to={link}
      className={`block p-6 rounded-lg transition-colors ${colorClasses[color]}`}
    >
      <div className="mb-3">{icon}</div>
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm opacity-80 mt-1">{description}</p>
    </Link>
  );
}
